import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, School, GraduationCap, UserCircle, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, completeLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Trim to eliminate accidental leading/trailing whitespace from copy-paste.
      const cleanUsername = (creds.username || '').trim();
      const cleanPassword = (creds.password || '').trim();
      const user = await login(cleanUsername, cleanPassword);
      if (user.role !== 'school_admin') {
        toast.error('This login is for the Office Admin only.');
        return;
      }
      if (user.must_change_password) {
        // Open mandatory change-password modal — user cannot proceed to dashboard
        // until they've set a new password. Mirrors HeadTeacherLogin behavior.
        setPasswordForm({ currentPassword: cleanPassword, newPassword: '', confirmPassword: '' });
        setShowPasswordChange(true);
        return;
      }
      toast.success(`Welcome, ${user.name}`);
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword === passwordForm.currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success('Password changed successfully! Welcome!');
      setShowPasswordChange(false);
      await completeLogin();
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-600 to-amber-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <Briefcase className="w-12 h-12 text-amber-700" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-amber-900" data-testid="admin-login-title">
            Office Admin Login
          </CardTitle>
          <CardDescription className="text-base">
            Admissions · Fees · Attendance · PIN resets
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-user">Username</Label>
              <Input
                id="admin-user"
                value={creds.username}
                onChange={(e) => setCreds({ ...creds, username: e.target.value })}
                placeholder="Enter your username"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-pwd">Password</Label>
              <div className="relative">
                <Input
                  id="admin-pwd"
                  type={showPassword ? 'text' : 'password'}
                  value={creds.password}
                  onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full py-6 text-lg bg-amber-600 hover:bg-amber-700"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? 'Logging in…' : 'Login as Office Admin'}
            </Button>
          </form>

          <div className="mt-6 space-y-2">
            <Link to="/head-teacher/login">
              <Button variant="outline" className="w-full">
                <School className="w-4 h-4 mr-2" />
                Principal / HT Login
              </Button>
            </Link>
            <Link to="/teacher-login">
              <Button variant="outline" className="w-full">
                <GraduationCap className="w-4 h-4 mr-2" />
                Teacher Login
              </Button>
            </Link>
            <Link to="/student-login">
              <Button variant="outline" className="w-full">
                <UserCircle className="w-4 h-4 mr-2" />
                Student Login
              </Button>
            </Link>
          </div>

          <div className="mt-6 p-4 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-800 font-semibold mb-2">Demo Credentials:</p>
            <p className="text-xs text-amber-700">Username: admin</p>
            <p className="text-xs text-amber-700">Password: admin123</p>
          </div>
        </CardContent>
      </Card>

      {/* Mandatory Change-Password Modal — Office Admin first login */}
      <Dialog open={showPasswordChange} onOpenChange={() => { /* mandatory — no close */ }}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-change-password-dialog" onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-amber-900">Set Your Password</DialogTitle>
            <DialogDescription>
              First-time login — please set a new password. You cannot skip this step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="At least 6 characters"
                data-testid="admin-new-password-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Re-type new password"
                data-testid="admin-confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="admin-save-password-btn"
            >
              {changingPassword ? 'Saving…' : 'Save Password & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogin;
